import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { BorderedTab, BorderedTabs, Box, Card, Divider, Flex, FlexColumn, Spinner, Tag, Text } from 'core/components';
import { Field, FormDialog, InputGroup, TextArea } from 'core/form';
import { fields } from 'app/forms/config/runbook';
import Markdown from 'app/components/markdown/Markdown';
import AlertPolicyAssignmentsTab, { setShowSelectedFilter } from './AlertPolicyAssignmentsTab';
import MarkdownPreviewTextArea from './MarkdownPreviewTextArea';
import RunbookComment from './RunbookComment';

const RunbookFormBody = observer(
  ({
    model,
    policies,
    loadingPolicies,
    showSelectedOnly,
    onShowSelectedToggle,
    tabsProps = {},
    readOnly = false,
    templatePolicies = [],
    showTemplatePolicies = true,
    onTemplateClick,
    canSave = true
  }) => {
    const isAssignmentsTabLoading = loadingPolicies || !policies;
    const selectedCount = policies?.selectedSize ?? 0;

    const readOnlyOverviewPanel = (
      <Flex flex={1} minHeight={0} overflowY="auto" overflowX="hidden">
        <FlexColumn flex={1} px={3} pt={1} gap={3}>
          <Flex alignItems="center" gap={3}>
            <FlexColumn gap={1} flex="0 0 auto">
              <Text fontWeight="bold" small>
                Runbook Title
              </Text>
              <Text>{model.get('name')}</Text>
            </FlexColumn>

            <Divider vertical style={{ alignSelf: 'stretch' }} />

            <FlexColumn gap={1} flex="0 0 auto">
              <Text fontWeight="bold" small>
                Last Modified
              </Text>
              <Text title={model.timeAgo}>
                {model.revisionAuthor ? `${model.revisionAuthor} on ${model.lastModifiedTime}` : model.lastModifiedTime}
              </Text>
            </FlexColumn>
            {model.lastModifiedComment ? (
              <>
                <Divider vertical style={{ alignSelf: 'stretch' }} />
                <FlexColumn gap={1} flex={1}>
                  <Text fontWeight="bold" small>
                    Last Modified Comment
                  </Text>
                  <Text>
                    <RunbookComment comment={model.lastModifiedComment} />
                  </Text>
                </FlexColumn>
              </>
            ) : null}
          </Flex>

          {model.get('description') ? (
            <FlexColumn gap={1}>
              <Text fontWeight="bold" small>
                Description
              </Text>
              <Text>{model.get('description')}</Text>
            </FlexColumn>
          ) : null}
          <FlexColumn flex={1} gap={1}>
            <Text fontWeight="bold" small>
              Instructions
            </Text>
            <Card flex={1} mb={3} p={0}>
              <FlexColumn gap={1} p={2}>
                {model.get('display_md') ? (
                  <Markdown>{model.get('display_md')}</Markdown>
                ) : (
                  <Text muted>No instructions provided.</Text>
                )}
              </FlexColumn>
            </Card>
          </FlexColumn>
        </FlexColumn>
        {showTemplatePolicies && (
          <FlexColumn width={260} flexShrink={0} borderLeft="thin" p={2} gap={1}>
            <Text fontWeight="bold" small mb={1}>
              Alert Policy Templates
            </Text>
            {templatePolicies.length > 0 ? (
              templatePolicies.map((policy) => (
                <Tag key={policy.id} minimal fill interactive onClick={() => onTemplateClick?.(policy)}>
                  {policy.name}
                </Tag>
              ))
            ) : (
              <Text small muted>
                No template assignments.
              </Text>
            )}
          </FlexColumn>
        )}
      </Flex>
    );

    const overviewPanel = (
      <FlexColumn px={2} flex={1} minHeight={0} overflowY="auto" overflowX="hidden">
        <Field name="name" large autoFocus rules="required">
          <InputGroup />
        </Field>
        <Field name="description" large>
          <TextArea rows="2" fill />
        </Field>
        <Field name="display_md" large showLabel={false}>
          {(fieldProps) => <MarkdownPreviewTextArea {...fieldProps} fillHeight />}
        </Field>
        {model && !model.isNew && (
          <Field name="comment" large mt={2}>
            <TextArea rows="2" fill />
          </Field>
        )}
      </FlexColumn>
    );

    return (
      <BorderedTabs defaultSelectedTabId="overview" tabIndent={16} mt={2} mb={1} flex={1} minHeight={0} {...tabsProps}>
        <BorderedTab id="overview" title="Overview" panel={readOnly ? readOnlyOverviewPanel : overviewPanel} />
        <BorderedTab
          id="alert-policy-assignments"
          title={
            <Flex alignItems="center">
              <Box mr={1}>Alert Policy Assignments</Box>
              <Tag small round textAlign="center" minWidth="20px">
                {isAssignmentsTabLoading ? <Spinner size={8} /> : selectedCount}
              </Tag>
            </Flex>
          }
          disabled={isAssignmentsTabLoading}
          panel={
            <AlertPolicyAssignmentsTab
              policies={policies}
              model={model}
              showSelectedOnly={showSelectedOnly}
              onShowSelectedToggle={onShowSelectedToggle}
              readOnly={!canSave}
            />
          }
        />
      </BorderedTabs>
    );
  }
);

@inject('$alerting', '$auth', '$investigationConfig', '$runbook')
@observer
class RunbookFormDialog extends Component {
  state = {
    showSelectedOnly: false
  };

  componentDidMount() {
    this.loadPolicySelections();
  }

  componentDidUpdate(prevProps) {
    const { policies } = this.props;
    if (!prevProps.policies && policies) {
      this.loadPolicySelections();
    }
  }

  loadPolicySelections = () => {
    const { model, policies, $runbook } = this.props;
    if (!model || model.isNew || !policies) {
      return;
    }

    const mappings = model.getMappingsOfType([$runbook.alertPolicyType, $runbook.alertPolicyTemplateType]);
    const matched = this.extractPolicies(mappings);
    if (matched.length > 0) {
      matched.forEach((m) => policies.select(m, { multi: true }));
    }

    const canSave = model.isNew ? $runbook.canCreateRunbooks : $runbook.canUpdateRunbooks;
    if (!canSave) {
      setShowSelectedFilter(policies, true);
      this.setState({ showSelectedOnly: true });
    }
  };

  extractPolicies = (mappings) => {
    const { policies, $alerting, $runbook } = this.props;
    const matched = new Map();

    (mappings || []).forEach(({ resource_id, resource_type }) => {
      let model;
      if (resource_type === $runbook.alertPolicyType) {
        model = $alerting.policyCollection.getPolicyByRuleId(resource_id);
      } else {
        const templateList = $alerting.policyTemplateCollection.get() || [];
        model =
          templateList.find((p) => String(p.ruleId) === String(resource_id)) ||
          templateList.find((p) => String(p.id) === String(resource_id));
      }

      const local = model ? policies.get(model.id) : policies.get(resource_id);
      if (local) {
        const key = local.ruleId ?? local.id;
        if (!matched.has(key)) {
          matched.set(key, local);
        }
      }
    });

    return Array.from(matched.values());
  };

  onSubmit = (form, values) => {
    const {
      model: currentModel,
      $auth,
      $investigationConfig,
      $runbook,
      resourceType,
      policies,
      toast = true,
      onSaveSuccess,
      skipConfigRefetch
    } = this.props;

    const type = resourceType ?? $runbook.alertPolicyType;

    const saveValues = {
      ...values,
      mappings: (policies?.selected ?? []).map((model) => ({
        resource_type: type,
        resource_id: String(model.ruleId)
      }))
    };

    return currentModel.save(saveValues, { optimistic: true, toast }).then(() => {
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      $runbook.collection.groupBy = $runbook.collection.defaultGroupBy;
      // $investigationConfig.collection.fetch short-circuits without the aiInvestigation permission.
      if (!skipConfigRefetch && $auth.isAdministrator) {
        $investigationConfig.collection.fetch({ force: true, skipPrefetch: true });
      }
    });
  };

  resetCommentField = (form) => {
    if (form) {
      form.setValue('comment', '', { validate: false });
    }
  };

  handleFormLoad = (form) => {
    this.resetCommentField(form);
  };

  handleShowSelectedToggle = (value) => {
    this.setState({ showSelectedOnly: value });
  };

  onCloseDialog = () => {
    const { onClose, policies } = this.props;
    if (policies) {
      policies.clearSelection();
      policies.clearFilters();
    }
    if (onClose) {
      onClose();
    }
  };

  render() {
    const {
      $runbook,
      isOpen,
      model,
      policies,
      loadingPolicies,
      readOnly: isPresetReadOnly = false,
      templatePolicies = [],
      onTemplateClick
    } = this.props;
    const { showSelectedOnly } = this.state;

    const isSaving = model && model.isRequestActive('updating');
    const canCreate = $runbook.canCreateRunbooks;
    const canUpdate = $runbook.canUpdateRunbooks;
    const canSave = model?.isNew ? canCreate : canUpdate;
    const isEffectivelyReadOnly = isPresetReadOnly || !canSave;

    return (
      <FormDialog
        {...this.props}
        fields={fields}
        entityName="Runbook"
        title={!canSave ? 'View Runbook' : undefined}
        options={{ name: 'Runbook Dialog Form' }}
        isOpen={isOpen}
        onClose={this.onCloseDialog}
        showFormActions={canSave}
        formActionsProps={{
          onSubmit: this.onSubmit,
          onCancel: this.onCloseDialog,
          showSave: canSave
        }}
        submitButtonProps={{
          loading: isSaving,
          text: isPresetReadOnly ? 'Save Assignments' : undefined
        }}
        formComponentProps={{ onLoad: this.handleFormLoad }}
        dialogBodyProps={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          style: { borderRadius: 'inherit' }
        }}
        style={{ maxWidth: 1600 }}
        height="90vh"
      >
        <RunbookFormBody
          model={model}
          policies={policies}
          loadingPolicies={loadingPolicies}
          showSelectedOnly={showSelectedOnly}
          onShowSelectedToggle={this.handleShowSelectedToggle}
          tabsProps={{ selectedTabBackgroundColor: 'dialogBackground' }}
          readOnly={isEffectivelyReadOnly}
          templatePolicies={templatePolicies}
          showTemplatePolicies={$runbook.isGlobalRunbooksEnabled}
          onTemplateClick={onTemplateClick}
          canSave={canSave}
        />
      </FormDialog>
    );
  }
}

export default RunbookFormDialog;
